import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, BookmarkPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { SavedListView } from '@/services/dialer/savedListViews';

/**
 * Active filters as removable pills, and saved views (W4, B2-liste).
 * The filters stay set in the toolbar; this row says, at all times, what
 * hides contacts from the list (search, call status, lead status, sort), and
 * removes any of them in one click.
 */
export interface ListPill { id: string; label: ReactNode; removeLabel: string; onRemove: () => void }

export function ListFilterPills({ pills, onClearAll, savedViews, activeSavedViewId, onApplySaved, onRemoveSaved, onSave }: {
  pills: ListPill[];
  onClearAll: () => void;
  savedViews: SavedListView[];
  activeSavedViewId: string | null;
  onApplySaved: (view: SavedListView) => void;
  onRemoveSaved: (view: SavedListView) => void;
  onSave: (name: string) => void;
}) {
  const { t } = useTranslation(['dialer', 'common']);
  const [saving, setSaving] = useState(false);
  if (pills.length === 0 && savedViews.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="list-filter-pills">
      {savedViews.length > 0 && (
        <ul className="flex flex-wrap items-center gap-1.5" aria-label={t('dialer:contacts.savedViews.label')}>
          {savedViews.map((v) => {
            const active = v.id === activeSavedViewId;
            return (
              <li key={v.id} className="inline-flex items-center rounded-full border h-8 text-[12px]"
                style={{ borderColor: active ? 'var(--vf-accent-bd)' : 'var(--vf-border)', background: active ? 'var(--vf-accent-bg)' : 'var(--vf-surface-1)' }}>
                <button type="button" onClick={() => onApplySaved(v)} aria-pressed={active}
                  aria-label={t('dialer:contacts.savedViews.apply', { name: v.name })}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full pl-2.5 pr-1 outline-none focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)]"
                  style={{ color: active ? 'var(--vf-accent)' : 'var(--vf-text-2)' }}>
                  <Bookmark className="h-3 w-3" aria-hidden />
                  {v.name}
                </button>
                <button type="button" onClick={() => onRemoveSaved(v)}
                  aria-label={t('dialer:contacts.savedViews.remove', { name: v.name })}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--vf-surface-3)]"
                  style={{ color: 'var(--vf-text-3)' }}>
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {pills.length > 0 && (
        <ul className="flex flex-wrap items-center gap-1.5" aria-label={t('dialer:contacts.pills.label')}>
          {pills.map((p) => (
            <li key={p.id} className="inline-flex items-center gap-1 rounded-full border pl-2.5 h-8 text-[12px]"
              style={{ borderColor: 'var(--vf-accent-bd)', background: 'var(--vf-accent-bg)', color: 'var(--vf-text-1)' }}>
              {p.label}
              <button type="button" onClick={p.onRemove} aria-label={p.removeLabel}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--vf-surface-3)]">
                <X className="h-3 w-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {pills.length > 1 && (
        <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>{t('dialer:contacts.empty.clearFilters')}</Button>
      )}
      {pills.length > 0 && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setSaving(true)}>
          <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t('dialer:contacts.savedViews.save')}
        </Button>
      )}
      <SaveViewDialog open={saving} onOpenChange={setSaving} onSave={(name) => { onSave(name); setSaving(false); }} />
    </div>
  );
}

export function SaveViewDialog({ open, onOpenChange, onSave }: { open: boolean; onOpenChange: (open: boolean) => void; onSave: (name: string) => void }) {
  const { t } = useTranslation(['dialer', 'common']);
  const [name, setName] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim());
    setName('');
  };
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) setName(''); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('dialer:contacts.savedViews.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('dialer:contacts.savedViews.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5 text-sm">
            <span>{t('dialer:contacts.savedViews.nameLabel')}</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus
              placeholder={t('dialer:contacts.savedViews.namePlaceholder')} />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
            <Button type="submit" disabled={!name.trim()}>{t('dialer:contacts.savedViews.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
